import { Actor, HttpAgent, type Identity } from '@icp-sdk/core/agent';
import { IDL } from '@icp-sdk/core/candid';
import { Principal } from '@icp-sdk/core/principal';
import { idlFactory, type _SERVICE as Backend } from '../bindings/backend/declarations/backend.did';
import { backend } from './backend';
import { HOST } from './api';
import { SNSC } from './native-token';

export type ReactionKind = 'rocket' | 'fire' | 'poop' | 'flag';
export type ReactionConfig = Awaited<ReturnType<Backend['reaction_config']>>;
export type ReactionTotals = Awaited<ReturnType<Backend['reaction_totals']>>;
export type ReactionQuote = Awaited<ReturnType<Backend['my_reaction_quote']>>[number];
export type ReactionDaily = Awaited<ReturnType<Backend['my_reaction_daily']>>;
type Account = ReactionQuote['treasury'];
type ReactionService = Pick<Backend, 'create_reaction_quote' | 'pay_reaction' | 'reaction_quote' | 'my_reaction_quote' | 'cancel_reaction_quote' | 'recover_reaction' | 'analytics_account_seen' | 'my_reaction_daily'>;
type Allowance = { allowance: bigint; expires_at: [] | [bigint] };
type Approval = {
  spender: Account; amount: bigint; from_subaccount: []; fee: [bigint]; memo: [Uint8Array];
  created_at_time: [bigint]; expected_allowance: [bigint]; expires_at: [bigint];
};
type ApprovalResult = { Ok: bigint } | { Err: Record<string, unknown> };
interface Ledger {
  icrc1_balance_of: (account: Account) => Promise<bigint>;
  icrc1_fee: () => Promise<bigint>;
  icrc2_allowance: (args: { account: Account; spender: Account }) => Promise<Allowance>;
  icrc2_approve: (args: Approval) => Promise<ApprovalResult>;
}
export interface ReactionWallet {
  owner: string; kind: 'ii' | 'plug'; label: string;
  readonly service: ReactionService; readonly ledger: Ledger; readonly config: ReactionConfig;
  readonly assertCurrent: () => Promise<void>;
}
interface Plug {
  requestConnect: (options: { whitelist: string[]; host: string; timeout: number }) => Promise<unknown>;
  agent?: { getPrincipal: () => Promise<Principal> };
  createActor: <T>(options: { canisterId: string; interfaceFactory: IDL.InterfaceFactory }) => Promise<T>;
}
const backendId = () => {
  const id = import.meta.env.VITE_BACKEND_CANISTER_ID;
  if (!id) throw Error('The Soonscreener payment canister is unavailable.');
  return Principal.fromText(id);
};
const local = () => import.meta.env.VITE_DFX_NETWORK === 'local';
const networkHost = () => local() ? import.meta.env.VITE_DFX_HOST : HOST;
const unwrap = <T,>(value: { ok: T } | { err: string }): T => { if ('err' in value) throw Error(value.err); return value.ok; };
const sameBytes = (a: Account['subaccount'], b: Account['subaccount']) => a.length === b.length && (!a.length || Array.from(a[0]!).join(',') === Array.from(b[0]!).join(','));
const sameAccount = (a: Account, b: Account) => a.owner.toText() === b.owner.toText() && sameBytes(a.subaccount, b.subaccount);
const treasuryBytes = Uint8Array.from([83, 78, 83, 67, 82, 69, 65, 67, 84, 73, 79, 78, 83, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0]);

function validateConfig(config: ReactionConfig) {
  if (config.price_e8s !== 100_000_000n || !('plus_network_fees' in config.fee_policy)) throw Error('The reaction price differs from the advertised 1 SNSC.');
  if (!local() && (config.test_mode || config.ledger.toText() !== SNSC.ledger || config.factory.toText() !== SNSC.factory)) throw Error('Reaction payments require the canonical SNSC ledger and Soon factory.');
  if (!config.test_mode && config.ledger.toText() !== SNSC.ledger) throw Error('The SNSC ledger is not verified.');
  if (!sameAccount(config.treasury, { owner: backendId(), subaccount: [treasuryBytes] })) throw Error('The reaction treasury account is not verified.');
}
function validateQuote(wallet: ReactionWallet, quote: ReactionQuote) {
  validateConfig(wallet.config);
  if (quote.owner.toText() !== wallet.owner || quote.ledger.toText() !== wallet.config.ledger.toText() || quote.test_mode !== wallet.config.test_mode) throw Error('This reaction belongs to a different payer or ledger.');
  if (quote.amount_e8s !== 100_000_000n || quote.allowance_e8s !== quote.amount_e8s + quote.ledger_fee_e8s || quote.total_debit_e8s !== quote.amount_e8s + quote.ledger_fee_e8s * 2n) throw Error('The reaction payment amounts are invalid.');
  if (!sameAccount(quote.spender, { owner: backendId(), subaccount: [] }) || !sameAccount(quote.treasury, wallet.config.treasury)) throw Error('The reaction payment recipient is not verified.');
}
function sameIntent(original: ReactionQuote, latest: ReactionQuote) {
  if (original.id !== latest.id || original.owner.toText() !== latest.owner.toText() || original.market.toText() !== latest.market.toText() || reactionKind(original.kind) !== reactionKind(latest.kind) || original.created_at !== latest.created_at || original.expires_at !== latest.expires_at || original.amount_e8s !== latest.amount_e8s || original.ledger_fee_e8s !== latest.ledger_fee_e8s || original.config_version !== latest.config_version || !sameAccount(original.treasury, latest.treasury)) throw Error('The original reaction payment changed. Review it again.');
}
export function reactionKind(kind: ReactionQuote['kind']): ReactionKind { return Object.keys(kind)[0] as ReactionKind; }
export function reactionStatus(quote: ReactionQuote): 'quoted' | 'pending' | 'uncertain' | 'paid' | 'failed' | 'cancelled' | 'expired' {
  if ('failed' in quote.status) {
    if (/cancelled|canceled/i.test(quote.status.failed)) return 'cancelled';
    if (/expired/i.test(quote.status.failed)) return 'expired';
    return 'failed';
  }
  return Object.keys(quote.status)[0] as 'quoted' | 'pending' | 'uncertain' | 'paid';
}
export async function reactionPublic(market: string) {
  const service = await backend();
  const [config, totals] = await Promise.all([service.reaction_config(), service.reaction_totals(Principal.fromText(market))]);
  validateConfig(config);
  return { config, totals };
}
const ledgerIdl: IDL.InterfaceFactory = ({ IDL: T }) => {
  const account = T.Record({ owner: T.Principal, subaccount: T.Opt(T.Vec(T.Nat8)) });
  return T.Service({
    icrc1_balance_of: T.Func([account], [T.Nat], ['query']),
    icrc1_fee: T.Func([], [T.Nat], ['query']),
    icrc2_allowance: T.Func([T.Record({ account, spender: account })], [T.Record({ allowance: T.Nat, expires_at: T.Opt(T.Nat64) })], ['query']),
    icrc2_approve: T.Func([T.Record({ spender: account, amount: T.Nat, from_subaccount: T.Opt(T.Vec(T.Nat8)), fee: T.Opt(T.Nat), memo: T.Opt(T.Vec(T.Nat8)), created_at_time: T.Opt(T.Nat64), expected_allowance: T.Opt(T.Nat), expires_at: T.Opt(T.Nat64) })], [T.Variant({ Ok: T.Nat, Err: T.Variant({ BadFee: T.Record({ expected_fee: T.Nat }), InsufficientFunds: T.Record({ balance: T.Nat }), AllowanceChanged: T.Record({ current_allowance: T.Nat }), Expired: T.Record({ ledger_time: T.Nat64 }), TooOld: T.Null, CreatedInFuture: T.Record({ ledger_time: T.Nat64 }), Duplicate: T.Record({ duplicate_of: T.Nat }), TemporarilyUnavailable: T.Null, GenericError: T.Record({ error_code: T.Nat, message: T.Text }) }) })], []),
  });
};
const reactionIdl: IDL.InterfaceFactory = args => {
  const full = idlFactory(args), methods: Record<string, IDL.FuncClass> = {};
  const permitted = new Set(['create_reaction_quote', 'pay_reaction', 'reaction_quote', 'my_reaction_quote', 'cancel_reaction_quote', 'recover_reaction', 'analytics_account_seen', 'my_reaction_daily']);
  for (const [name, fn] of full._fields) if (permitted.has(name)) methods[name] = fn;
  return args.IDL.Service(methods);
};
export async function reactionWalletII(identity: Identity, assertCurrent: () => void): Promise<ReactionWallet> {
  assertCurrent();
  const owner = identity.getPrincipal().toText();
  if (identity.getPrincipal().isAnonymous()) throw Error('Sign in to pay for a reaction.');
  const service = await backend(identity), config = await service.reaction_config(); validateConfig(config); assertCurrent();
  const agent = await HttpAgent.create({ host: networkHost(), identity });
  if (local()) await agent.fetchRootKey();
  assertCurrent();
  return { owner, kind: 'ii', label: 'Internet Identity wallet', service, config, ledger: Actor.createActor<Ledger>(ledgerIdl, { agent, canisterId: config.ledger }), assertCurrent: async () => { assertCurrent(); if (identity.getPrincipal().toText() !== owner) throw Error('Your payment account changed.'); } };
}
export async function reactionWalletPlug(assertCurrent: () => void): Promise<ReactionWallet> {
  const plug = (window as Window & { ic?: { plug?: Plug } }).ic?.plug;
  if (!plug) throw Error('Install or enable the Plug browser extension to use that wallet.');
  // Only an explicit wallet-selection click reaches this permission request.
  const config = await (await backend()).reaction_config(); validateConfig(config); assertCurrent();
  const accepted = await plug.requestConnect({ whitelist: [backendId().toText(), config.ledger.toText()], host: networkHost(), timeout: 50_000 });
  if (accepted === false) throw Error('Plug request was cancelled.');
  assertCurrent();
  if (!plug.agent) throw Error('Plug did not provide a wallet. Unlock it and try again.');
  const owner = (await plug.agent.getPrincipal()).toText();
  if (Principal.fromText(owner).isAnonymous()) throw Error('Choose a signed Plug wallet.');
  const verify = async () => { assertCurrent(); if (!plug.agent || (await plug.agent.getPrincipal()).toText() !== owner) throw Error('Your Plug account changed. Review the payment again.'); assertCurrent(); };
  const service = await plug.createActor<ReactionService>({ canisterId: backendId().toText(), interfaceFactory: reactionIdl }); await verify();
  // A signed account observation has no token or allowance effect.
  if (typeof service.analytics_account_seen === 'function') void service.analytics_account_seen({ plug: null }).then(result => { if ('ok' in result) window.dispatchEvent(new Event('soonscreener:analytics-updated')); }).catch(() => undefined);
  const ledger = await plug.createActor<Ledger>({ canisterId: config.ledger.toText(), interfaceFactory: ledgerIdl }); await verify();
  return { owner, kind: 'plug', label: 'Plug wallet', service, config, ledger, assertCurrent: verify };
}

// Only public request data is persisted: never a key, identity or signature.
type Attempt = { phase: 'approving' | 'approved' | 'paying'; createdAt: string; expectedAllowance: string };
const attemptKey = (wallet: ReactionWallet, quote: ReactionQuote) => `soonscreener:reaction-approval:${backendId().toText()}:${quote.ledger.toText()}:${wallet.owner}:${quote.id}`;
function readAttempt(wallet: ReactionWallet, quote: ReactionQuote): Attempt | null {
  const raw = localStorage.getItem(attemptKey(wallet, quote));
  if (!raw) return null;
  try {
    const value = JSON.parse(raw) as Attempt;
    if (!['approving', 'approved', 'paying'].includes(value.phase) || !/^\d+$/.test(value.createdAt) || !/^\d+$/.test(value.expectedAllowance)) throw Error('Invalid saved payment');
    return value;
  } catch { throw Error('The saved reaction payment needs recovery. Keep the original reaction open.'); }
}
function writeAttempt(wallet: ReactionWallet, quote: ReactionQuote, value: Attempt) {
  // Fail before signing if the browser cannot preserve an uncertain request.
  localStorage.setItem(attemptKey(wallet, quote), JSON.stringify(value));
}
function clearAttempt(wallet: ReactionWallet, quote: ReactionQuote) { try { localStorage.removeItem(attemptKey(wallet, quote)); } catch { /* A terminal canister receipt takes precedence. */ } }
export function reactionNeedsRecovery(wallet: ReactionWallet, quote: ReactionQuote) {
  if (['paid', 'failed', 'cancelled', 'expired'].includes(reactionStatus(quote))) return false;
  if (['pending', 'uncertain'].includes(reactionStatus(quote))) return true;
  try { const attempt = readAttempt(wallet, quote); return Boolean(attempt && attempt.phase !== 'approved'); } catch { return true; }
}
const payer = (wallet: ReactionWallet): Account => ({ owner: Principal.fromText(wallet.owner), subaccount: [] });
const exactAllowance = (allowance: Allowance, quote: ReactionQuote) => allowance.allowance === quote.allowance_e8s && allowance.expires_at[0] !== undefined && allowance.expires_at[0] >= quote.expires_at;
async function latestQuote(wallet: ReactionWallet, original: ReactionQuote) {
  await wallet.assertCurrent(); validateQuote(wallet, original);
  let latest = (await wallet.service.reaction_quote(original.id))[0]; await wallet.assertCurrent();
  if (!latest) {
    // A query can lag. Only a replicated update may establish that this past
    // intent has no outstanding payment. Pending/uncertain records never prune.
    const closed = await wallet.service.cancel_reaction_quote(original.id); await wallet.assertCurrent();
    if ('ok' in closed) {
      clearAttempt(wallet, original);
      return { ...original, status: { failed: 'Reaction record retired. Check your SNSC ledger history for any earlier completed payment.' } } as ReactionQuote;
    }
    latest = (await wallet.service.reaction_quote(original.id))[0]; await wallet.assertCurrent();
    if (!latest) throw Error(closed.err);
  }
  validateQuote(wallet, latest); sameIntent(original, latest); return latest;
}
export async function currentReaction(wallet: ReactionWallet) {
  await wallet.assertCurrent(); const quote = (await wallet.service.my_reaction_quote())[0]; await wallet.assertCurrent();
  if (!quote) return null;
  validateQuote(wallet, quote);
  if (['paid', 'failed', 'cancelled', 'expired'].includes(reactionStatus(quote))) clearAttempt(wallet, quote);
  return quote;
}
export async function reactionDaily(wallet: ReactionWallet, market: string): Promise<ReactionDaily> {
  await wallet.assertCurrent();
  const daily = await wallet.service.my_reaction_daily(Principal.fromText(market));
  await wallet.assertCurrent();
  if (daily.test_mode !== wallet.config.test_mode) throw Error('The reaction payment configuration changed. Reconnect this wallet.');
  return daily;
}
export function reactionResetUTC(daily: ReactionDaily): string {
  if (!daily.used || daily.next_allowed_at === 0n) return '';
  return new Date(Number(daily.next_allowed_at / 1_000_000n)).toLocaleString('en-GB', { timeZone: 'UTC', dateStyle: 'medium', timeStyle: 'short' }) + ' UTC';
}
async function assertDailyAvailable(wallet: ReactionWallet, market: string, existingQuote = false) {
  const daily = await reactionDaily(wallet, market);
  if (daily.used) throw Error(`This wallet has already reacted to this token today. Resets ${reactionResetUTC(daily)}.`);
  // A quoted intent itself reserves the caller's payment slot. Continuing that
  // same quote is allowed; original uncertain payments always use recovery.
  if (!existingQuote && !daily.available) throw Error(daily.pending ? 'Finish or recover your original reaction payment before starting another.' : 'The daily reaction allowance is unavailable. Reconnect this wallet.');
}
export async function quoteReaction(wallet: ReactionWallet, market: string, kind: ReactionKind) {
  await wallet.assertCurrent();
  await assertDailyAvailable(wallet, market);
  const result = unwrap(await wallet.service.create_reaction_quote(Principal.fromText(market), { [kind]: null } as ReactionQuote['kind']));
  await wallet.assertCurrent(); validateQuote(wallet, result); return result;
}
export async function reviewReaction(wallet: ReactionWallet, quote: ReactionQuote) {
  await wallet.assertCurrent(); validateQuote(wallet, quote);
  if (reactionStatus(quote) === 'quoted' && !reactionNeedsRecovery(wallet, quote)) await assertDailyAvailable(wallet, quote.market.toText(), true);
  const [balance, allowance, fee] = await Promise.all([wallet.ledger.icrc1_balance_of(payer(wallet)), wallet.ledger.icrc2_allowance({ account: payer(wallet), spender: quote.spender }), wallet.ledger.icrc1_fee()]);
  await wallet.assertCurrent();
  if (fee !== quote.ledger_fee_e8s) throw Error('The SNSC ledger fee changed. Recover or cancel this quote before requesting another.');
  return { balance, allowance: allowance.allowance, requiredDebit: exactAllowance(allowance, quote) ? quote.allowance_e8s : quote.total_debit_e8s };
}
function approvalRequest(quote: ReactionQuote, attempt: Attempt): Approval {
  const memo = new Uint8Array(32); memo.set(new TextEncoder().encode('SNSC_REACTION_APPROVE'));
  let id = quote.id; for (let index = 31; index >= 24; index--) { memo[index] = Number(id & 255n); id >>= 8n; }
  return { spender: quote.spender, amount: quote.allowance_e8s, from_subaccount: [], fee: [quote.ledger_fee_e8s], memo: [memo], created_at_time: [BigInt(attempt.createdAt)], expected_allowance: [BigInt(attempt.expectedAllowance)], expires_at: [quote.expires_at] };
}
async function approve(wallet: ReactionWallet, quote: ReactionQuote) {
  await wallet.assertCurrent();
  if (quote.expires_at <= BigInt(Date.now()) * 1_000_000n) throw Error('The reaction quote expired before payment. Recover the original quote.');
  const existing = await wallet.ledger.icrc2_allowance({ account: payer(wallet), spender: quote.spender }); await wallet.assertCurrent();
  const stored = readAttempt(wallet, quote);
  if (exactAllowance(existing, quote)) {
    writeAttempt(wallet, quote, { phase: 'approved', createdAt: stored?.createdAt ?? quote.created_at.toString(), expectedAllowance: stored?.expectedAllowance ?? existing.allowance.toString() }); return;
  }
  const attempt: Attempt = stored ?? { phase: 'approving', createdAt: quote.created_at.toString(), expectedAllowance: existing.allowance.toString() };
  const [balance, fee] = await Promise.all([wallet.ledger.icrc1_balance_of(payer(wallet)), wallet.ledger.icrc1_fee()]); await wallet.assertCurrent();
  if (fee !== quote.ledger_fee_e8s) throw Error('The SNSC ledger fee changed. Recover or cancel this reaction.');
  if (balance < quote.total_debit_e8s) throw Error('This wallet needs 1 SNSC plus both ledger fees for the reaction.');
  writeAttempt(wallet, quote, { ...attempt, phase: 'approving' });
  let result: ApprovalResult;
  try { result = await wallet.ledger.icrc2_approve(approvalRequest(quote, attempt)); }
  catch {
    await wallet.assertCurrent();
    try {
      if (exactAllowance(await wallet.ledger.icrc2_allowance({ account: payer(wallet), spender: quote.spender }), quote)) {
        await wallet.assertCurrent(); writeAttempt(wallet, quote, { ...attempt, phase: 'approved' }); return;
      }
    } catch { /* Preserve the immutable approval request until explicit recovery. */ }
    throw Error('The approval reply is unclear. Recover this original reaction before paying again.');
  }
  await wallet.assertCurrent();
  if ('Err' in result && !('Duplicate' in result.Err)) {
    clearAttempt(wallet, quote);
    throw Error(`SNSC approval failed: ${JSON.stringify(result.Err, (_, value) => typeof value === 'bigint' ? value.toString() : value)}`);
  }
  // A duplicate is not proof of an unspent allowance: check it before pulling.
  if (!exactAllowance(await wallet.ledger.icrc2_allowance({ account: payer(wallet), spender: quote.spender }), quote)) throw Error('The original approval has no matching allowance. Recover the reaction before paying again.');
  await wallet.assertCurrent(); writeAttempt(wallet, quote, { ...attempt, phase: 'approved' });
}
async function submit(wallet: ReactionWallet, quote: ReactionQuote) {
  await wallet.assertCurrent();
  const prior = readAttempt(wallet, quote) ?? { phase: 'approved' as const, createdAt: quote.created_at.toString(), expectedAllowance: quote.allowance_e8s.toString() };
  writeAttempt(wallet, quote, { ...prior, phase: 'paying' });
  try {
    const result = unwrap(await wallet.service.pay_reaction(quote.id)); await wallet.assertCurrent(); validateQuote(wallet, result); sameIntent(quote, result);
    if (['paid', 'failed', 'cancelled', 'expired'].includes(reactionStatus(result))) clearAttempt(wallet, result);
    if (reactionStatus(result) === 'paid') notifyReactionPaid(result);
    return result;
  } catch {
    await wallet.assertCurrent();
    try {
      const saved = await latestQuote(wallet, quote);
      if (reactionStatus(saved) !== 'quoted') { if (['paid', 'failed', 'cancelled', 'expired'].includes(reactionStatus(saved))) clearAttempt(wallet, saved); if (reactionStatus(saved) === 'paid') notifyReactionPaid(saved); return saved; }
    } catch { /* Never replace an unresolved intent after a missing reply. */ }
    throw Error('The reaction payment reply is unclear. Recover this original reaction; do not pay for a replacement.');
  }
}
async function serialized<T>(wallet: ReactionWallet, work: () => Promise<T>): Promise<T> {
  const key = `soonscreener:reaction-payment:${backendId().toText()}:${wallet.owner}`;
  return 'locks' in navigator ? navigator.locks.request(key, work) : work();
}
export async function payReaction(wallet: ReactionWallet, quote: ReactionQuote) {
  return serialized(wallet, async () => {
    const latest = await latestQuote(wallet, quote);
    if (['paid', 'failed', 'cancelled', 'expired'].includes(reactionStatus(latest))) { clearAttempt(wallet, latest); if (reactionStatus(latest) === 'paid') notifyReactionPaid(latest); return latest; }
    if (reactionStatus(latest) !== 'quoted' || reactionNeedsRecovery(wallet, latest)) return recoverInner(wallet, latest);
    await assertDailyAvailable(wallet, latest.market.toText(), true);
    await approve(wallet, latest); return submit(wallet, latest);
  });
}
async function recoverInner(wallet: ReactionWallet, original: ReactionQuote) {
  const quote = await latestQuote(wallet, original), status = reactionStatus(quote);
  if (['paid', 'failed', 'cancelled', 'expired'].includes(status)) { clearAttempt(wallet, quote); if (status === 'paid') notifyReactionPaid(quote); return quote; }
  if (status === 'quoted') {
    if (quote.expires_at <= BigInt(Date.now()) * 1_000_000n) {
      unwrap(await wallet.service.cancel_reaction_quote(quote.id)); await wallet.assertCurrent(); clearAttempt(wallet, quote);
      return await latestQuote(wallet, quote);
    }
    const attempt = readAttempt(wallet, quote);
    if (attempt?.phase === 'paying') return submit(wallet, quote);
    // Explicit recovery resends the exact stored approval, never a fresh nonce.
    if (attempt) { await approve(wallet, quote); return submit(wallet, quote); }
    return quote;
  }
  const recovered = unwrap(await wallet.service.recover_reaction(quote.id)); await wallet.assertCurrent(); validateQuote(wallet, recovered); sameIntent(quote, recovered);
  if (reactionStatus(recovered) === 'paid') { clearAttempt(wallet, recovered); notifyReactionPaid(recovered); return recovered; }
  if (['pending', 'uncertain'].includes(reactionStatus(recovered))) return submit(wallet, recovered);
  return recovered;
}
export async function recoverReaction(wallet: ReactionWallet, quote: ReactionQuote) { return serialized(wallet, () => recoverInner(wallet, quote)); }
function notifyReactionPaid(quote: ReactionQuote) {
  window.dispatchEvent(new Event('premium-access-changed'));
  window.dispatchEvent(new CustomEvent('soonscreener:reactions-updated', { detail: { market: quote.market.toText() } }));
}
export async function cancelReaction(wallet: ReactionWallet, id: bigint) {
  await wallet.assertCurrent();
  const quote = (await wallet.service.reaction_quote(id))[0]; await wallet.assertCurrent();
  if (!quote) { unwrap(await wallet.service.cancel_reaction_quote(id)); await wallet.assertCurrent(); return; }
  validateQuote(wallet, quote);
  if (reactionNeedsRecovery(wallet, quote)) throw Error('Recover the original payment before cancelling or replacing it.');
  unwrap(await wallet.service.cancel_reaction_quote(id)); await wallet.assertCurrent(); clearAttempt(wallet, quote);
}
